/******************************************************************************

Copyright (c) 2020-2021, Visual Elements AS

******************************************************************************/

// @format

//////////////////////////////////////////////////////////////////////////////

/** The main highcharts editor namespace
 * @ignore
 */

 let highed = {
  isReady: false,
  initQueue: [],
 };

/** Convert a string to a bool
 *  @namespace highed
 *  @param {string} what - the string to convert
 *  @return {bool} - the resulting bool
 */
export function toBool(what) {
  return what === 'true' || what === true || what === 'on';
}

export function isEmptyObjectArray(arr) {
  return (
    isObj(arr[0]) &&
    arr.some(function (b) {
      return Object.keys(b).length === 0;
    })
  );
}

export function isObj(what) {
  return what && what.constructor.toString().indexOf('Object') > -1;
}

/** Deep merge two objects.
 * Note: this modifies object `a`!
 * @namespace highed
 * @param {object} a - the destination object
 * @param {object} b - the source object
 * @param {bool} ignoreEmpty - Ignore empty things
 * @param {object} excludeMap - Map of properties to exclude from the merge
 * @return {object} - argument a
 */
export function merge(a, b, ignoreEmpty, excludeMap) {
  if (!a || !b) return a || b;

  if (ignoreEmpty && Object.keys(b).length === 0) {
    return;
  }

  Object.keys(b).forEach(function (bk) {
    if (excludeMap && excludeMap[bk]) {
    } else if (isNull(b[bk]) || isBasic(b[bk])) {
      a[bk] = b[bk];
    } else if (isArr(b[bk])) {
      if (isEmptyObjectArray(b[bk])) return;
      a[bk] = [];

      b[bk].forEach(function (i) {
        if (isNull(i) || isBasic(i)) {
          a[bk].push(i);
        } else {
          a[bk].push(merge(isArr(i) ? [] : {}, i));
        }
      });
    } else if (
      b[bk].tagName &&
      b[bk].appendChild &&
      b[bk].removeChild &&
      b[bk].style
    ) {
      a[bk] = b[bk];
    } else {
      if (ignoreEmpty && Object.keys(b[bk]).length === 0) {
        return;
      }

      a[bk] = a[bk] || {};
      merge(a[bk], b[bk]);
    }
  });
  return a;
}

/** Check if something is null or undefined
 *  @namespace highed
 *  @param {anything} what - the value to check
 *  @return {bool} - true if null
 */
export function isNull(what) {
  return typeof what === 'undefined' || what === null;
}

/** Check if something is a string
 *  @namespace highed
 *  @param {anything} what - the value to check
 *  @return {bool} - true if string
 */
export function isStr(what) {
  return typeof what === 'string' || what instanceof String;
}

/** Check if something is a number
 * @namespace highed
 *  @param {anything} what - the value to check
 *  @return {bool} - true if number
 */
export function isNum(what) {
  return !isNaN(parseFloat(what)) && isFinite(what);
}

/** Check if a value is a function
 * @namespace highed
 * @param {anything} what - the value to check
 * @return {bool} - true if function
 */
export function isFn(what) {
  return (what && typeof what === 'function') || what instanceof Function;
}

/** Check if a value is an array
 * @namespace highed
 * @param {anything} what - the value to check
 * @return {bool} - true if array
 */
export function isArr(what) {
  return !isNull(what) && what.constructor.toString().indexOf('Array') > -1;
}

/** Check if a value is a boolean
 * @namespace highed
 * @param {anything} what - the value to check
 * @return {bool} - true if bool
 */
export function isBool(what) {
  return what === true || what === false;
}

/** Check if a value is a basic type
 * A basic type is either a bool, string, or a number
 * @namespace highed
 * @param {anything} what - the value to check
 * @return {bool} - true if basic
 */
export function isBasic(what) {
  return (
    !isArr(what) && (isStr(what) || isNum(what) || isBool(what) || isFn(what))
  );
}

///////////////////////////////////////////////////////////////////////////

export function pollForReady() {
  if (!highed.isReady) {
    if (document.body) {
      highed.isReady = true;
      highed.initQueue.forEach(function (fn) {
        fn();
      });
    } else {
      window.setTimeout(pollForReady, 100);
    }
  }
}

pollForReady();

///////////////////////////////////////////////////////////////////////////

/** Add a function to call when the document is ready
 * @param {function} fn - the function to call
 */
export function ready(fn) {
  if (isFn(fn)) {
    if (highed.isReady) {
      fn();
    } else {
      highed.initQueue.push(fn);
    }
  }
}